import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Helmet } from "react-helmet-async";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, Check, Mail, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const LOGO_URL = `/api/static/images/web/logo_mbs.png`;

function AuthShell({ children }) {
  return (
    <div className="min-h-screen bg-[#FBF9F5] text-[#333333] flex items-center justify-center px-4 py-10 relative overflow-hidden">
      {/* Subtle ambient accents */}
      <div aria-hidden="true" className="absolute inset-0 bg-tech-grid opacity-60" />
      <div aria-hidden="true" className="absolute -top-40 -left-32 w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle,rgba(196,106,74,0.08),transparent_60%)]" />
      <div aria-hidden="true" className="absolute -bottom-40 -right-32 w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle,rgba(0,140,168,0.08),transparent_60%)]" />
      <div className="relative w-full max-w-sm">{children}</div>
    </div>
  );
}

function AuthHeader() {
  return (
    <div className="text-center mb-10">
      <Link to="/" className="inline-flex items-center gap-2.5" aria-label="Retour à l'accueil">
        <img src={LOGO_URL} alt="MonBeauSite" className="h-12 w-12 object-contain rounded-xl ring-1 ring-[#D4AF37]/25 shadow-sm bg-white" data-testid="login-logo" />
        <span className="text-[17px] font-semibold text-[#1F3535] tracking-tight font-body">
          MonBeauSite<span className="text-[#C46A4A]">.</span>com
        </span>
      </Link>
      <div className="mt-5 flex items-center justify-center gap-2.5" aria-hidden="true">
        <span className="h-px w-10 bg-gradient-to-r from-transparent to-[#D4AF37]/50" />
        <span className="w-1 h-1 rounded-full bg-[#D4AF37]/60" />
        <span className="h-px w-10 bg-gradient-to-l from-transparent to-[#D4AF37]/50" />
      </div>
    </div>
  );
}

const inputCls =
  "mt-2 bg-white border-[#BFBFBF]/70 h-11 text-[#1F3535] placeholder:text-[#6B6B6B]/45 rounded-xl focus-visible:ring-2 focus-visible:ring-[#008CA8]/40 focus-visible:border-[#008CA8] transition-shadow font-body";
const labelCls = "text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-semibold nav-text";
const primaryBtn =
  "w-full bg-[#1F3535] text-white hover:bg-[#0F2424] h-11 rounded-full font-semibold tracking-[0.16em] uppercase text-[11px] shadow-md shadow-[#1F3535]/25 hover:shadow-lg hover:-translate-y-px transition-all";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot | forgot-sent
  const [forgotLoading, setForgotLoading] = useState(false);
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      await checkAuth();
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Veuillez saisir votre adresse email");
      return;
    }
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setMode("forgot-sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <AuthShell>
      <Helmet>
        <title>Connexion — MonBeauSite</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <AuthHeader />

      <div className="tech-bracket bg-white/90 backdrop-blur-sm border border-[#BFBFBF]/45 rounded-2xl px-7 py-8 shadow-sm shadow-[#1F3535]/[0.04]">
        {mode === "login" && (
          <>
            <div className="mb-6">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#008CA8] mb-2 inline-flex items-center gap-1.5">
                <Lock className="w-3 h-3" strokeWidth={2} /> SECURE_LOGIN // ESPACE CLIENT
              </p>
              <h1 className="text-2xl font-medium text-[#1F3535] font-montserrat tracking-tight leading-snug">
                Heureux de vous <span className="font-playfair italic text-[#C46A4A] font-normal">retrouver.</span>
              </h1>
            </div>
            <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
              <div>
                <Label className={labelCls}>Adresse email</Label>
                <Input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                  placeholder="votre@email.com"
                  required
                  autoComplete="email"
                />
              </div>
              <div>
                <Label className={labelCls}>Mot de passe</Label>
                <Input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputCls}
                  placeholder="Votre mot de passe"
                  required
                  autoComplete="current-password"
                />
              </div>
              <Button data-testid="login-submit-btn" type="submit" disabled={loading} className={primaryBtn}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
              </Button>
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => setMode("forgot")}
                  className="text-[11px] text-[#6B6B6B] hover:text-[#C46A4A] transition-colors font-body"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            </form>
          </>
        )}

        {mode === "forgot" && (
          <form onSubmit={handleForgotPassword} className="space-y-5" data-testid="forgot-password-form">
            <div className="text-center mb-2">
              <div className="w-12 h-12 rounded-full bg-[#C46A4A]/10 border border-[#C46A4A]/20 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-5 h-5 text-[#C46A4A]" strokeWidth={1.6} />
              </div>
              <h2 className="text-xl font-medium text-[#1F3535] font-montserrat tracking-tight">
                Mot de passe <span className="font-playfair italic text-[#C46A4A] font-normal">oublié</span>
              </h2>
              <p className="text-xs text-[#6B6B6B] mt-3 leading-relaxed font-body font-light">
                Saisissez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
              </p>
            </div>
            <div>
              <Label className={labelCls}>Adresse email</Label>
              <Input
                data-testid="forgot-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="votre@email.com"
                required
                autoFocus
              />
            </div>
            <Button data-testid="forgot-submit-btn" type="submit" disabled={forgotLoading} className={primaryBtn}>
              {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer le lien"}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => setMode("login")}
                className="inline-flex items-center gap-1.5 text-[11px] text-[#6B6B6B] hover:text-[#C46A4A] transition-colors font-body"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
              </button>
            </div>
          </form>
        )}

        {mode === "forgot-sent" && (
          <div className="text-center space-y-5 py-2" data-testid="forgot-sent-confirmation">
            <div className="w-14 h-14 rounded-full bg-[#1F3535]/[0.06] border border-[#D4AF37]/30 flex items-center justify-center mx-auto">
              <Check className="w-6 h-6 text-[#1F3535]" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-xl font-medium text-[#1F3535] font-montserrat tracking-tight">
                Email <span className="font-playfair italic text-[#C46A4A] font-normal">envoyé</span>
              </h2>
              <p className="text-sm text-[#555555] mt-3 leading-relaxed font-body font-light">
                Si l'adresse <strong className="text-[#1F3535] font-mono-tech">{email}</strong> est associée à un compte, vous recevrez un lien de réinitialisation dans quelques instants.
              </p>
              <p className="text-[11px] text-[#6B6B6B]/70 mt-3 italic font-body">
                Pensez à vérifier vos spams.
              </p>
            </div>
            <button
              onClick={() => {
                setMode("login");
                setEmail("");
              }}
              className="inline-flex items-center gap-1.5 text-[11px] text-[#6B6B6B] hover:text-[#C46A4A] transition-colors font-body"
              data-testid="back-to-login-after-sent"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
            </button>
          </div>
        )}
      </div>

      <div className="mt-8 text-center">
        <Link to="/" className="inline-flex items-center gap-1.5 text-[11px] text-[#6B6B6B] hover:text-[#1F3535] transition-colors font-body">
          <Lock className="w-3 h-3 text-[#D4AF37]" /> ← Retour au site
        </Link>
      </div>
    </AuthShell>
  );
}
